export type TypeFilter = 'ALL' | 'SOCIAL' | 'SPORT' | 'HELP' | 'COIN';
export type TimeFilter = 'TODAY' | 'WEEK' | 'UPCOMING';


interface FilterBarProps {
  selectedType: TypeFilter;
  onTypeChange: (t: TypeFilter) => void;
  selectedDistance: number;
  onDistanceChange: (d: number) => void;
  selectedTime: TimeFilter;
  onTimeChange: (t: TimeFilter) => void;
  resultCount: number;
}

const typeOptions: { value: TypeFilter; label: string; icon: string }[] = [
  { value: 'ALL', label: 'Tous', icon: 'ri-apps-2-line' },
  { value: 'SOCIAL', label: 'Sortir', icon: 'ri-cup-line' },
  { value: 'SPORT', label: 'Bouger', icon: 'ri-run-line' },
  { value: 'HELP', label: "Entraide", icon: 'ri-hand-heart-line' },
  // { value: 'COIN', label: 'Le Coin', icon: 'ri-chat-smile-3-line' }, Desactivé temporairement
];

const distanceOptions = [1, 5, 10, 15];

const timeOptions: { value: TimeFilter; label: string }[] = [
  { value: 'TODAY', label: "Aujourd'hui" },
  { value: 'WEEK', label: 'Cette semaine' },
  { value: 'UPCOMING', label: 'À venir' },
];

export default function FilterBar({ selectedType, onTypeChange, selectedDistance, onDistanceChange, selectedTime, onTimeChange, resultCount, }: FilterBarProps) {
  return (
    <div className="flex flex-col gap-4 min-w-0">
      {/* Type segmented control */}
      <div className="w-full overflow-x-auto no-scrollbar md:px-1">
        <div className="grid grid-cols-4 md:inline-flex md:items-center gap-0.5 p-1 bg-background-100 rounded-full md:whitespace-nowrap">
          {typeOptions.map((opt) => {
            const active = selectedType === opt.value;

            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onTypeChange(opt.value)}
                className={`flex items-center justify-center gap-[0.375rem] px-[0.2rem] py-[0.4rem] rounded-full text-[0.75rem] font-bold transition-colors cursor-pointer min-w-0 whitespace-nowrap
                   md:justify-start md:gap-1.5 md:px-4 md:text-sm md:font-semibold md:whitespace-nowrap md:shrink-0
                  ${active
                    ? 'bg-white text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-800'
                  }`}
              >
                <i className={`${opt.icon} text-[12px] md:text-base`}></i>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Distance + time + count */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 max-[430px]:gap-x-[.9rem] flex-wrap max-[430px]:grid max-[430px]:grid-cols-[auto_auto_1fr] max-[430px]:items-center">

            {/* Colonne 1 */}
            <span className="text-xs font-medium text-foreground-500 whitespace-nowrap max-[430px]:text-[.85rem] max-[430px]:font-semibold">
              Rayon
            </span>

            {/* Colonne 2 */}
            <div className="flex items-center gap-1">
              {distanceOptions.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => onDistanceChange(d)}
                  className={`px-3 py-1.5 rounded-full text-[.85rem] font-semibold transition-colors cursor-pointer whitespace-nowrap
                  max-[430px]:text-[.75rem]
                  max-[430px]:font-bold
                  max-[430px]:px-2
                  max-[430px]:py-1
                  ${selectedDistance === d
                      ? 'bg-foreground-900 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                >
                  {d} km
                </button>
              ))}
            </div>

            {/* Colonne 3 : valeur + range */}
            <div className="flex items-center gap-2 max-[430px]:flex-col max-[430px]:items-start max-[430px]:gap-0">

              <strong className="text-[.85rem] font-semibold text-foreground-900 whitespace-nowrap min-w-[42px] max-[430px]:text-[.65rem] max-[430px]:font-bold">
                {selectedDistance} km
              </strong>

              <input
                type="range"
                min={11}
                max={60}
                step={1}
                value={Math.min(60, Math.max(11, selectedDistance))}
                onChange={(e) => onDistanceChange(Number(e.target.value))}
                className="w-28 md:w-36 accent-primary-500 cursor-pointer max-[430px]:w-full"
                aria-label="Rayon personnalisé de 11 à 60 kilomètres"
              />

            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[.85rem] font-medium text-foreground-500 whitespace-nowrap">
              Quand
            </span>

            <div className="flex items-center gap-1">
              {timeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onTimeChange(opt.value)}
                  className={`px-3 py-1.5 rounded-full text-[10px] min-[375px]:text-[.75rem] font-semibold transition-colors cursor-pointer whitespace-nowrap ${selectedTime === opt.value
                    ? 'bg-foreground-900 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <span className="hidden md:block text-sm text-foreground-500">
          <strong className="text-foreground-900 font-semibold">
            {resultCount}
          </strong>{' '}
          {resultCount > 1 ? 'publications' : 'publication'} autour de toi
        </span>
      </div>
    </div>
  );
}